import * as React from "react";
import {Fragment, useEffect, useMemo, useRef, useState} from "react";
import ReactDOM from 'react-dom';
import {toast} from "react-toastify";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";
import useDebounce from "@/hooks/useDebounce";
import {
	checkAccountVerificationStatusApi,
	exportClaimRequestsApi,
	getDistinctFamilyTreeNamesApi,
	getPaginatedClaimRequestsApi,
	updateClaimRequestApi,
	updateMatchedAccountContactApi,
} from "@/service/admin/profileClaimRequestApi";
import {
	AdjustmentsHorizontalIcon,
	ArrowDownTrayIcon,
	ArrowPathIcon,
	CheckCircleIcon,
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	ExclamationTriangleIcon,
	FunnelIcon,
	MagnifyingGlassIcon,
	XCircleIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import {CheckCircleIcon as CheckCircleSolid} from "@heroicons/react/24/solid";
import {Popover, Transition} from "@headlessui/react";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {ClipboardDocumentIcon} from "@heroicons/react/16/solid";
import {FlyoutSuperUserNavigationMenu} from "@/components/admin/FlyoutSuperUserNavigationMenu";
import {countryCodes} from "@/data/CountryCode";
import useDelayedClose from "@/hooks/useDelayedClose";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & CONFIGS
// ─────────────────────────────────────────────────────────────────────────────

const BLANK_AWARE_OPTIONS = [
	{label: "Has a value", value: "__not_blank__"},
	{label: "Is blank", value: "__blank__"},
];

const STATUS_OPTIONS = [
	{label: "Pending", value: "PENDING"},
	{label: "No Match", value: "NO_MATCH"},
	{label: "No Contact", value: "NO_CONTACT"},
	{label: "Already Registered", value: "ALREADY_REGISTERED"},
	{label: "Auto Resolved", value: "AUTO_RESOLVED"},
	{label: "Resolved", value: "RESOLVED"},
	{label: "Rejected", value: "REJECTED"},
];

const REJECTION_REASONS = [
	"Spam / Fake submission",
	"Duplicate submission",
	"Not a community member",
	"Opted out",
	"Other",
];

const CLAIM_STATUS_TABS = [
	{id: "all", label: "All Claims"},
	{id: "pending", label: "Pending"},
	{id: "no_match", label: "No Match"},
	{id: "no_contact", label: "No Contact"},
	{id: "resolved", label: "Resolved"},
	{id: "rejected", label: "Rejected"},
];

const COLUMN_LABELS = {
	submittedPhone: "Phone",
	submittedEmail: "Email",
	submittedFamilyTreeName: "Family Tree",
	status: "Status",
	matchedAccountId: "Matched Account",
	adminNotes: "Admin Notes",
};

// Status badge styles
const STATUS_BADGE = {
	PENDING: "bg-amber-50  text-amber-700  border-amber-200",
	NO_MATCH: "bg-red-50    text-red-700    border-red-200",
	NO_CONTACT: "bg-orange-50 text-orange-700 border-orange-200",
	ALREADY_REGISTERED: "bg-blue-50   text-blue-700   border-blue-200",
	AUTO_RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
	RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
	REJECTED: "bg-gray-100  text-gray-500   border-gray-200",
};

const STATUS_LABEL = {
	PENDING: "Pending",
	NO_MATCH: "No Match",
	NO_CONTACT: "No Contact",
	ALREADY_REGISTERED: "Already Registered",
	AUTO_RESOLVED: "Auto Resolved",
	RESOLVED: "Resolved",
	REJECTED: "Rejected",
};

const getScrollbarWidth = (el) => el.offsetWidth - el.clientWidth;

const DEFAULT_COUNTRY = countryCodes.find(c => c.countryCode === 'IN') ?? countryCodes[0];
const makePhoneState = (country = DEFAULT_COUNTRY) => ({
	charCode: country.countryCode,
	dialingCode: country.countryDialingCode,
	countryFlagSrc: country.countryFlagSrc,
	countryName: country.countryName,
	number: '',
});

const StatusBadge = ({status}) => (
	<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs
        font-custom-semi-bold border ${STATUS_BADGE[status] ?? "bg-gray-100 text-gray-500 border-gray-200"}`}>
        {STATUS_LABEL[status] ?? status}
    </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES  (mirrors YellowPageOnboardingForm patterns exactly)
// ─────────────────────────────────────────────────────────────────────────────

const Field = ({children, error}) => (
	<div className="flex flex-col gap-1">
		{children}
		{error && (
			<p className="flex items-center gap-1 font-custom-medium text-[0.68rem] text-red-500 mt-0.5">
				<span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block shrink-0"/>
				{error}
			</p>
		)}
	</div>
);

const InputLabel = ({htmlFor, children, required}) => (
	<label htmlFor={htmlFor} className="font-custom-semi-bold text-[0.75rem] text-gray-600 tracking-wide">
		{children}{required && <span className="text-red-400 ml-0.5">*</span>}
	</label>
);

const inputCls = (hasError) =>
	`w-full px-3.5 py-2.5 text-sm rounded-xl border outline-none transition-all duration-200 font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
	${hasError
		? 'border-red-300 ring-2 ring-red-100 focus:ring-red-200'
		: 'border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10 hover:border-gray-300'}`;

// ─────────────────────────────────────────────────────────────────────────────
// PhoneRow — ④ keyboard-navigable country-code dropdown
// ─────────────────────────────────────────────────────────────────────────────
const PhoneRow = ({
	                  value,
	                  onChange,
	                  countryCode,
	                  onCountryChange,
	                  error,
	                  placeholder = "9320638137",
	                  allCountryCodes
                  }) => {
	const [open, setOpen] = useState(false);
	const {shouldRender: showDrop, isClosing: dropClosing} = useDelayedClose(open);
	const [dropPos, setDropPos] = useState({top: 0, left: 0, width: 0});
	const [focusedIdx, setFocusedIdx] = useState(-1);
	const listRef = useRef(null);
	const btnRef = useRef(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const scrollParent = btnRef.current?.closest('.overflow-y-auto');
		if (!scrollParent) return;
		if (open) {
			const sbWidth = getScrollbarWidth(scrollParent);
			const existingPadding = parseFloat(getComputedStyle(scrollParent).paddingRight) || 0;
			scrollParent.style.overflow = 'hidden';
			scrollParent.style.paddingRight = `${existingPadding + sbWidth}px`;
		} else {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		}
		return () => {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		};
	}, [open]);

	useEffect(() => {
		if (open && btnRef.current) {
			const r = btnRef.current.getBoundingClientRect();
			setDropPos({top: r.bottom + 4, left: r.left, width: r.width * 3});
			// width * 3 approximates the min-w-[11.25rem]; adjust to taste
		}
	}, [open]);

	useEffect(() => {
		if (focusedIdx >= 0 && listRef.current)
			listRef.current.children[focusedIdx]?.scrollIntoView({block: 'nearest'});
	}, [focusedIdx]);

	useEffect(() => {
		const handler = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				closeDropdown();
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [open]);

	// ✅ CHANGED: returnFocus param — only Escape returns focus, Tab does not
	const closeDropdown = (returnFocus = false) => {
		setOpen(false);
		setFocusedIdx(-1);
		if (returnFocus) btnRef.current?.focus();
	};

	const handleKeyDown = (e) => {
		if (!open) {
			if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
				e.preventDefault();
				setOpen(true);
				setFocusedIdx(0);
			}
			return;
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setFocusedIdx(i => Math.min(i + 1, allCountryCodes.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setFocusedIdx(i => Math.max(i - 1, 0));
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (focusedIdx >= 0) {
					onCountryChange(allCountryCodes[focusedIdx]);
					closeDropdown(true);
				}
				break;
			case 'Escape':
				e.preventDefault();
				closeDropdown(true);
				break;
			case 'Tab':
				closeDropdown();
				break;
		}
	};

	return (
		<Field error={error}>
			<div ref={containerRef} className="relative flex items-stretch">
				<button
					ref={btnRef} type="button"
					role="combobox" aria-expanded={open} aria-haspopup="listbox"
					onClick={() => {
						setOpen(v => !v);
						setFocusedIdx(open ? -1 : 0);
					}}
					onKeyDown={handleKeyDown}
					className="flex items-center gap-1.5 px-3 border border-r-0 rounded-l-xl bg-gray-50 hover:bg-gray-100 transition-colors shrink-0"
					style={{borderColor: error ? '#FCA5A5' : '#E5E7EB'}}>
					<img src={countryCode?.countryFlagSrc || allCountryCodes[0].countryFlagSrc} alt="flag"
					     className="w-4 h-3 rounded-sm object-cover"/>
					<span className="font-custom-bold text-[0.72rem] text-gray-700">
						{countryCode?.dialingCode || DEFAULT_COUNTRY.countryDialingCode}
					</span>
					<ChevronDownIcon
						className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}/>
				</button>

				<input type="number"
				       className={`flex-1 px-3.5 py-2.5 text-sm rounded-r-xl border outline-none transition-all duration-200 font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
					       ${error ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10'}`}
				       placeholder={placeholder} value={value}
				       onChange={e => onChange(e.target.value)}/>


				{showDrop && ReactDOM.createPortal(
					<div
						style={{
							position: 'fixed',
							top: dropPos.top,
							left: dropPos.left,
							width: dropPos.width,
							zIndex: 9999
						}}
						className={`${dropClosing ? 'dropdown-close' : 'dropdown-open'} overflow-hidden bg-white rounded-xl shadow-xl border border-gray-100`}>
						<div ref={listRef} role="listbox" onWheel={e => e.stopPropagation()}
						     className="overflow-y-auto max-h-32">
							{allCountryCodes.map((c, i) => (
								<button key={i} type="button" role="option"
								        aria-selected={countryCode?.charCode === c.countryCode}
								        className={`w-full flex items-center gap-2 px-3 py-2.5 font-custom-medium text-[0.75rem] transition-colors
								        ${focusedIdx === i ? 'bg-orange-50 text-[#D26A53]' : 'text-gray-700 hover:bg-gray-50'}`}
								        onMouseEnter={() => setFocusedIdx(i)}
								        onClick={() => {
									        onCountryChange(c);
									        closeDropdown(true);
								        }}>
									<img src={c.countryFlagSrc} alt={c.countryCode}
									     className="w-4 h-3 rounded-sm object-cover"/>
									{c.countryName} ({c.countryDialingCode})
								</button>
							))}
						</div>
					</div>,
					document.body
				)}
			</div>
		</Field>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// COLUMN FILTER POPOVER  (identical pattern to Users.jsx)
// ─────────────────────────────────────────────────────────────────────────────

const ColumnFilterPopover = ({columnId, currentValue, onFilterChange, configOptions}) => {
	const [pendingSet, setPendingSet] = useState(
		() => new Set(Array.isArray(currentValue) ? currentValue : [])
	);

	if (!configOptions) return null;

	const isActive = Array.isArray(currentValue) && currentValue.length > 0;

	const toggleOption = (value) => {
		setPendingSet(prev => {
			const next = new Set(prev);
			if (next.has(value)) next.delete(value);
			else next.add(value);
			return next;
		});
	};

	const pendingArray = Array.from(pendingSet);

	return (
		<Popover className="relative inline-flex">
			{({open}) => (
				<>
					<Popover.Button
						className={`p-0.5 rounded transition-colors outline-none
                            ${isActive ? "text-[#D26A53]" : open ? "text-gray-700 bg-gray-100" : "text-gray-400 hover:text-gray-600"}`}
						onClick={(e) => e.stopPropagation()}
					>
						<FunnelIcon className={`h-3.5 w-3.5 ${isActive ? "fill-[#D26A53]/20" : ""}`}/>
					</Popover.Button>

					<Transition
						as={Fragment}
						enter="transition duration-100 ease-out"
						enterFrom="transform scale-95 opacity-0"
						enterTo="transform scale-100 opacity-100"
						leave="transition duration-75 ease-out"
						leaveFrom="transform scale-100 opacity-100"
						leaveTo="transform scale-95 opacity-0"
					>
						<Popover.Panel
							className="absolute top-full left-0 z-20 mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-xl"
							onClick={(e) => e.stopPropagation()}
						>
							{({close}) => (
								<div className="flex flex-col">
									<div
										className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 flex items-center justify-between">
										<p className="text-[10px] font-custom-semi-bold text-gray-400 tracking-wider">Filter</p>
										{pendingSet.size > 0 && (
											<button onClick={() => setPendingSet(new Set())}
											        className="text-[10px] text-[#D26A53] font-custom-semi-bold hover:underline">
												Clear
											</button>
										)}
									</div>
									<div className="flex flex-col max-h-52 overflow-y-auto py-1 thin-scrollbar">
										{configOptions.map((opt) => {
											const checked = pendingSet.has(opt.value);
											const isBlankOpt = opt.value === "__blank__" || opt.value === "__not_blank__";
											return (
												<label key={opt.value}
												       className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer
                                                        hover:bg-gray-50 transition-colors select-none
                                                        ${checked ? "bg-[#D26A53]/5" : ""}
                                                        ${isBlankOpt ? "text-gray-400 italic" : "text-gray-700"}`}>
													<input type="checkbox" checked={checked}
													       onChange={() => toggleOption(opt.value)}
													       className="rounded border-gray-300 text-[#D26A53] focus:ring-[#D26A53] h-3.5 w-3.5 shrink-0 cursor-pointer"/>
													<span
														className="text-xs font-custom-medium truncate">{opt.label}</span>
												</label>
											);
										})}
									</div>
									<div className="flex gap-2 p-2 border-t border-gray-100">
										<button
											onClick={() => {
												setPendingSet(new Set(Array.isArray(currentValue) ? currentValue : []));
												close();
											}}
											className="flex-1 px-2 py-1.5 text-xs font-custom-semi-bold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
											Cancel
										</button>
										<button
											onClick={() => {
												onFilterChange(columnId, pendingArray.length > 0 ? pendingArray : undefined);
												close();
											}}
											className="flex-1 px-2 py-1.5 text-xs font-custom-semi-bold text-white bg-[#D26A53] hover:bg-[#b85b46] rounded-lg transition-colors flex items-center justify-center gap-1">
											Apply
											{pendingSet.size > 0 && (
												<span
													className="bg-white/25 rounded px-1 text-[10px]">{pendingSet.size}</span>
											)}
										</button>
									</div>
								</div>
							)}
						</Popover.Panel>
					</Transition>
				</>
			)}
		</Popover>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE CONTACT DETAILS MODAL
// ─────────────────────────────────────────────────────────────────────────────

const UpdateContactModal = ({claim, matchedAccountId, visible, onClose, onSaved, axiosPrivate}) => {
	const [newPhone, setNewPhone] = useState(makePhoneState());
	const [newEmail, setNewEmail] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const handleSave = async () => {
		if (!newPhone.number && !newEmail.trim()) {
			toast.error("Please provide at least a phone number or email.");
			return;
		}
		setIsSaving(true);
		try {
			const res = await updateMatchedAccountContactApi(axiosPrivate, claim._id, {
				matchedAccountId,
				phone: newPhone.number || undefined,
				dialingCode: newPhone.number ? newPhone.dialingCode : undefined,
				email: newEmail.trim() || undefined,
			});
			if (res.success) {
				onSaved();
			} else {
				toast.error(res.message ?? "Failed to update.");
			}
		} catch {
			toast.error("Failed to update contact details.");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-[210] flex items-center justify-center p-4 transition-opacity duration-220"
			style={{
				background: "rgba(0,0,0,0.4)",
				backdropFilter: "blur(2px)",
				opacity: visible ? 1 : 0,
			}}
		>
			<div
				className="bg-white rounded-2xl w-full max-w-md flex flex-col overflow-hidden transition-all duration-220"
				style={{
					boxShadow: "0 32px 64px rgba(0,0,0,0.22)",
					transform: visible ? "scale(1) translateY(0)" : "scale(0.96) translateY(8px)",
					opacity: visible ? 1 : 0,
				}}
			>
				{/* Header */}
				<div className="shrink-0 px-6 pt-5 pb-4 bg-white"
				     style={{borderBottom: "1px solid #F3F3F3"}}>
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0 pr-3">
							<div className="flex items-center gap-2 mb-0.5">
								<div className="w-2 h-2 rounded-full shrink-0" style={{background: "#A20000"}}/>
								<p className="font-custom-bold text-[0.6rem] text-gray-400 uppercase tracking-widest">
									Profile Claims
								</p>
							</div>
							<h2 className="font-custom-extra-bold text-base text-gray-900 tracking-tight">
								Update Contact Details
							</h2>
							<p className="font-custom-regular text-[0.72rem] text-gray-500 mt-0.5">
								Update the account's contact details so the user can log in.
							</p>
						</div>
						<button onClick={onClose}
						        className="w-8 h-8 flex items-center justify-center rounded-full
                                transition-colors hover:bg-gray-100 shrink-0"
						        style={{border: "1.5px solid #E5E7EB"}}>
							<XMarkIcon className="w-4 h-4 text-gray-500"/>
						</button>
					</div>
				</div>

				{/* Body */}
				<div className="flex-1 overflow-y-auto px-6 py-5 thin-scrollbar flex flex-col gap-4">

					{/* Info banner */}
					<div className="flex gap-3 items-start p-3.5 rounded-xl"
					     style={{background: "#FFF7ED", border: "1px solid #FED7AA"}}>
						<ExclamationTriangleIcon className="h-5 w-5 text-orange-500 shrink-0 mt-0.5"/>
						<p className="font-custom-medium text-[0.75rem] text-orange-800 leading-relaxed">
							After saving, ask the user to log in using their updated details.
							Their account will be marked as verified automatically once they do.
						</p>
					</div>

					{/* Claim person summary */}
					<div className="flex flex-col gap-1 p-3.5 rounded-xl"
					     style={{background: "#FAFAFA", border: "1.5px solid #E5E7EB"}}>
						<p className="font-custom-bold text-sm text-gray-900">
							{[claim.submittedFirstName, claim.submittedMiddleName, claim.submittedLastName]
								.filter(Boolean).join(" ") || "—"}
						</p>
						<p className="font-custom-medium text-[0.72rem] text-gray-500">
							Account ID: <span className="font-mono">{matchedAccountId?.slice(-12)}</span>
						</p>
					</div>

					{/* New phone */}
					<div className="flex flex-col gap-1">
						<InputLabel>New Phone Number</InputLabel>
						<PhoneRow
							value={newPhone.number}
							onChange={v => setNewPhone(p => ({...p, number: v}))}
							countryCode={newPhone}
							onCountryChange={c => setNewPhone({
								charCode: c.countryCode,
								dialingCode: c.countryDialingCode,
								countryFlagSrc: c.countryFlagSrc,
								countryName: c.countryName,
								number: newPhone.number,
							})}
							placeholder="New phone number"
							allCountryCodes={countryCodes}
						/>
					</div>

					{/* New email */}
					<div className="flex flex-col gap-1">
						<InputLabel htmlFor="uc-email">New Email</InputLabel>
						<input
							id="uc-email"
							type="email"
							value={newEmail}
							onChange={e => setNewEmail(e.target.value)}
							placeholder="New email address"
							className={inputCls(false)}
						/>
					</div>
				</div>

				{/* Footer */}
				<div className="shrink-0 px-6 py-4 bg-white"
				     style={{borderTop: "1px solid #F3F3F3"}}>
					<div className="flex items-center justify-between gap-3">
						<button onClick={onClose} disabled={isSaving}
						        className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-600
                                transition-all duration-150 hover:bg-gray-100 disabled:opacity-50"
						        style={{border: "1.5px solid #E5E7EB"}}>
							← Cancel
						</button>
						<button
							onClick={handleSave}
							disabled={isSaving || (!newPhone.number && !newEmail.trim())}
							className="flex items-center gap-2 px-6 py-2.5 rounded-xl transform-gpu
                                font-custom-bold text-sm text-white transition-all duration-150
                                hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
							style={{
								background: "linear-gradient(135deg, #A20000, #8a0000)",
								boxShadow: "0 4px 14px rgba(162,0,0,0.30)",
							}}
						>
							{isSaving
								? <>
									<div
										className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0"/>
									Saving…</>
								: "Save & Notify User"
							}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// RESOLVE MODAL
// ─────────────────────────────────────────────────────────────────────────────

const ResolveModal = ({claim, visible, onClose, onResolved, axiosPrivate}) => {
	const [accountIdInput, setAccountIdInput] = useState(claim.matchedAccountId ?? "");

	const [showUpdateModal, setShowUpdateModal] = useState(false);
	const [updateModalVisible, setUpdateModalVisible] = useState(false);
	const [contactUpdated, setContactUpdated] = useState(false);
	const updateModalRafRef = useRef(null);

	const [verifyStatus, setVerifyStatus] = useState(null);
	const [adminNotes, setAdminNotes] = useState(claim.adminNotes ?? "");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isChecking, setIsChecking] = useState(false);

	useEffect(() => {
		if (claim.matchedAccountId) handleCheck(claim.matchedAccountId);
	}, []);

	const openUpdateModal = () => {
		setShowUpdateModal(true);
		cancelAnimationFrame(updateModalRafRef.current);
		updateModalRafRef.current = requestAnimationFrame(() => {
			updateModalRafRef.current = requestAnimationFrame(() => setUpdateModalVisible(true));
		});
	};

	const closeUpdateModal = () => {
		setUpdateModalVisible(false);
		setTimeout(() => setShowUpdateModal(false), 220);
	};

	// Cleanup
	useEffect(() => () => cancelAnimationFrame(updateModalRafRef.current), []);

	const handleCheck = async (id) => {
		const val = (id ?? accountIdInput).trim();
		if (!val) return;
		setIsChecking(true);
		setVerifyStatus(null);
		try {
			const res = await checkAccountVerificationStatusApi(axiosPrivate, val);
			setVerifyStatus(res);
		} catch {
			setVerifyStatus("error");
		} finally {
			setIsChecking(false);
		}
	};

	const canResolve = verifyStatus?.success && verifyStatus?.verified === true;

	const handleResolve = async () => {
		setIsSubmitting(true);
		try {
			const res = await updateClaimRequestApi(axiosPrivate, claim._id, {
				action: "resolve",
				matchedAccountId: (accountIdInput || claim.matchedAccountId)?.trim(),
				adminNotes,
			});
			if (res.success) {
				toast.success("Claim request resolved successfully!");
				onResolved();
			} else {
				toast.error(res.message ?? "Failed to resolve.");
			}
		} catch {
			toast.error("Failed to resolve. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-220"
			style={{
				background: "rgba(0,0,0,0.55)",
				backdropFilter: "blur(4px)",
				opacity: visible ? 1 : 0,
			}}
		>
			<div
				className="form-modal-container bg-white rounded-2xl w-full max-w-lg flex flex-col overflow-hidden transition-all duration-220"
				style={{
					boxShadow: "0 32px 64px rgba(0,0,0,0.22)",
					transform: visible ? "scale(1) translateY(0)" : "scale(0.96) translateY(8px)",
					opacity: visible ? 1 : 0,
				}}
			>
				{/* Header */}
				<div className="shrink-0 px-6 pt-5 pb-4 bg-white" style={{borderBottom: "1px solid #F3F3F3"}}>
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0 pr-3">
							<div className="flex items-center gap-2 mb-0.5">
								<div className="w-2 h-2 rounded-full shrink-0" style={{background: "#A20000"}}/>
								<p className="font-custom-bold text-[0.6rem] text-gray-400 uppercase tracking-widest">
									Profile Claims
								</p>
							</div>
							<h2 className="font-custom-extra-bold text-base text-gray-900 tracking-tight">
								Resolve Claim Request
							</h2>
							<p className="font-custom-regular text-[0.72rem] text-gray-500 mt-0.5">
								Verify the matched account before marking as resolved.
							</p>
						</div>
						<button
							onClick={onClose}
							className="w-8 h-8 flex items-center justify-center rounded-full
                                transition-colors hover:bg-gray-100 shrink-0"
							style={{border: "1.5px solid #E5E7EB"}}
						>
							<XMarkIcon className="w-4 h-4 text-gray-500"/>
						</button>
					</div>
				</div>

				{/* Body */}
				<div className="flex-1 overflow-y-auto px-6 py-5 thin-scrollbar flex flex-col gap-4">

					{/* Claim summary card */}
					<div className="flex flex-col gap-1 p-3.5 rounded-xl"
					     style={{background: "#FAFAFA", border: "1.5px solid #E5E7EB"}}>
						<p className="font-custom-bold text-sm text-gray-900">
							{[claim.submittedFirstName, claim.submittedMiddleName, claim.submittedLastName]
								.filter(Boolean).join(" ") || "—"}
						</p>
						<p className="font-custom-medium text-[0.72rem] text-gray-500">
							{[claim.submittedPhone, claim.submittedEmail, claim.submittedFamilyTreeName]
								.filter(Boolean).join(" · ") || "—"}
						</p>
					</div>

					{/* Account ID input */}
					<div className="flex flex-col gap-1">
						<InputLabel required>Matched Account ID</InputLabel>
						<p className="font-custom-regular text-[0.68rem] text-gray-400 -mt-0.5">
							Copy from the ID column in the Users section.
						</p>
						<div className="flex gap-2">
							<input
								type="text"
								value={accountIdInput}
								onChange={e => {
									setAccountIdInput(e.target.value);
									setVerifyStatus(null);
								}}
								disabled={!!claim.matchedAccountId}
								placeholder="Paste account _id here…"
								className={inputCls(false) + " flex-1"}
								style={claim.matchedAccountId ? {background: "#FAFAFA", color: "#6B7280"} : {}}
							/>
							{!claim.matchedAccountId && (
								<button
									onClick={() => handleCheck()}
									disabled={!accountIdInput.trim() || isChecking}
									className="px-4 py-2.5 text-sm font-custom-semi-bold text-white
                                        rounded-xl disabled:opacity-50 transition-colors shrink-0"
									style={{background: "linear-gradient(135deg, #A20000, #8a0000)"}}
								>
									{isChecking ? "Checking…" : "Check"}
								</button>
							)}
						</div>
					</div>

					{/* Verification result */}
					{isChecking && (
						<div className="flex items-center gap-2 p-3.5 rounded-xl"
						     style={{background: "#F9FAFB", border: "1px solid #E5E7EB"}}>
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#A20000] shrink-0"/>
							<p className="font-custom-medium text-[0.75rem] text-gray-500">Checking account status…</p>
						</div>
					)}

					{!isChecking && verifyStatus && verifyStatus !== "error" && (
						<>
							<div className={`flex gap-3 items-start p-3.5 rounded-xl`}
							     style={verifyStatus.verified
								     ? {background: "#F0FDF4", border: "1px solid #BBF7D0"}
								     : {background: "#FFFBEB", border: "1px solid #FDE68A"}}>
								{verifyStatus.verified
									? <CheckCircleSolid className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5"/>
									: <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 shrink-0 mt-0.5"/>
								}
								<div className="flex-1 min-w-0">
									{verifyStatus.verified ? (
										<>
											<p className="font-custom-semi-bold text-sm text-emerald-800">
												Account Verified ✓
											</p>
											<p className="font-custom-medium text-[0.72rem] text-emerald-700 mt-0.5 truncate">
												{verifyStatus.account?.name}
												{verifyStatus.account?.familyTree
													? ` · ${verifyStatus.account.familyTree}` : ""}
											</p>
										</>
									) : (
										<>
											<p className="font-custom-semi-bold text-sm text-amber-800">
												Not yet Verified
											</p>
											<p className="font-custom-medium text-[0.72rem] text-amber-700 mt-0.5 leading-relaxed">
												{verifyStatus.account?.name
													? `${verifyStatus.account.name} exists but hasn't completed verification. Ask them to log in first.`
													: verifyStatus.message}
											</p>

											{/* Footer row with both actions */}
											<div className="flex items-center gap-2 mt-2">
												<button
													onClick={() => {
														setVerifyStatus(null);
														setIsChecking(true);
														setTimeout(() => handleCheck(accountIdInput), 500);
													}}
													className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
									                    font-custom-semi-bold text-amber-800 transition-colors
									                    hover:bg-amber-100"
													style={{border: "1px solid #FDE68A"}}
												>
													<ArrowPathIcon className="h-3.5 w-3.5"/>
													Refresh
												</button>

												{verifyStatus.account && !contactUpdated && (
													<button
														type="button"
														onClick={openUpdateModal}
														className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                                                            font-custom-semi-bold text-white transition-all hover:opacity-90"
														style={{
															background: "linear-gradient(135deg, #A20000, #8a0000)",
														}}
													>
														Update Contact Details
													</button>
												)}
											</div>
										</>
									)}
								</div>
							</div>

							{!verifyStatus.verified && verifyStatus.account && contactUpdated && (
								<div className="flex flex-col gap-3">
									<div className="flex gap-3 items-start p-3.5 rounded-xl"
									     style={{background: "#EFF6FF", border: "1px solid #BFDBFE"}}>
										<CheckCircleSolid className="h-5 w-5 text-blue-500 shrink-0 mt-0.5"/>
										<div className="flex-1 min-w-0">
											<p className="font-custom-semi-bold text-sm text-blue-800">
												Contact details updated!
											</p>
											<p className="font-custom-medium text-[0.72rem] text-blue-700 mt-0.5 leading-relaxed">
												Ask the user to log in using their new contact details. Once they
												log in,
												click "Refresh" above to confirm their account is verified, then
												resolve.
											</p>
										</div>
									</div>
								</div>
							)}
						</>
					)}

					{!isChecking && verifyStatus === "error" && (
						<div className="flex gap-3 items-start p-3.5 rounded-xl"
						     style={{background: "#FEF2F2", border: "1px solid #FECACA"}}>
							<XMarkIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5"/>
							<p className="font-custom-medium text-[0.75rem] text-red-700 leading-relaxed">
								No account found with this ID. Please verify the ID from the Users section.
							</p>
						</div>
					)}

					{/* Admin notes */}
					<div className="flex flex-col gap-1">
						<InputLabel>
							Admin Notes
							<span className="font-custom-regular text-gray-400 ml-1">(optional)</span>
						</InputLabel>
						<textarea
							rows={3}
							value={adminNotes}
							onChange={e => setAdminNotes(e.target.value)}
							placeholder="Add any notes for future reference…"
							className={inputCls(false) + " resize-none"}
						/>
					</div>
				</div>

				{/* Footer */}
				<div className="shrink-0 px-6 py-4 bg-white" style={{borderTop: "1px solid #F3F3F3"}}>
					<div className="flex items-center justify-between gap-3">
						<button
							onClick={onClose}
							disabled={isSubmitting}
							className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-600
                                transition-all duration-150 hover:bg-gray-100 disabled:opacity-50"
							style={{border: "1.5px solid #E5E7EB"}}
						>
							← Cancel
						</button>
						<button
							onClick={handleResolve}
							disabled={!canResolve || isSubmitting}
							className="flex items-center gap-2 px-6 py-2.5 rounded-xl transform-gpu
                                font-custom-bold text-sm text-white transition-all duration-150
                                hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
							style={{
								background: "linear-gradient(135deg, #16A34A, #15803D)",
								boxShadow: canResolve ? "0 4px 14px rgba(22,163,74,0.30)" : "none",
							}}
						>
							{isSubmitting
								? <>
									<div
										className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0"/>
									Resolving…</>
								: <><CheckCircleSolid className="h-4 w-4 shrink-0"/> Mark as Resolved</>
							}
						</button>
					</div>
				</div>
			</div>

			{showUpdateModal && (
				<UpdateContactModal
					claim={claim}
					matchedAccountId={(accountIdInput || claim.matchedAccountId)?.trim()}
					axiosPrivate={axiosPrivate}
					visible={updateModalVisible}
					onClose={closeUpdateModal}
					onSaved={() => {
						setContactUpdated(true);
						closeUpdateModal();
					}}
				/>
			)}
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// REJECT MODAL
// ─────────────────────────────────────────────────────────────────────────────

const RejectModal = ({claim, visible, onClose, onRejected, axiosPrivate}) => {
	const [rejectionReason, setRejectionReason] = useState("");
	const [rejectionNote, setRejectionNote] = useState("");
	const [adminNotes, setAdminNotes] = useState(claim.adminNotes ?? "");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleReject = async () => {
		if (!rejectionReason) {
			toast.error("Please select a rejection reason.");
			return;
		}
		if (rejectionReason === "Other" && !rejectionNote.trim()) {
			toast.error("Please provide a note for 'Other' reason.");
			return;
		}
		setIsSubmitting(true);
		try {
			const res = await updateClaimRequestApi(axiosPrivate, claim._id, {
				action: "reject",
				rejectionReason,
				rejectionNote: rejectionReason === "Other" ? rejectionNote : undefined,
				adminNotes,
			});
			if (res.success) {
				toast.success("Claim request rejected.");
				onRejected();
			} else {
				toast.error(res.message ?? "Failed to reject.");
			}
		} catch {
			toast.error("Failed to reject. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-220"
			style={{
				background: "rgba(0,0,0,0.55)",
				backdropFilter: "blur(4px)",
				opacity: visible ? 1 : 0,
			}}
		>
			<div
				className="bg-white rounded-2xl w-full max-w-md flex flex-col overflow-hidden transition-all duration-220"
				style={{
					boxShadow: "0 32px 64px rgba(0,0,0,0.22)",
					transform: visible ? "scale(1) translateY(0)" : "scale(0.96) translateY(8px)",
					opacity: visible ? 1 : 0,
				}}
			>
				{/* Header */}
				<div className="shrink-0 px-6 pt-5 pb-4 bg-white" style={{borderBottom: "1px solid #F3F3F3"}}>
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0 pr-3">
							<div className="flex items-center gap-2 mb-0.5">
								<div className="w-2 h-2 rounded-full shrink-0" style={{background: "#A20000"}}/>
								<p className="font-custom-bold text-[0.6rem] text-gray-400 uppercase tracking-widest">
									Profile Claims
								</p>
							</div>
							<h2 className="font-custom-extra-bold text-base text-gray-900 tracking-tight">
								Reject Claim Request
							</h2>
							<p className="font-custom-regular text-[0.72rem] text-gray-500 mt-0.5">
								Select a reason for rejecting this request.
							</p>
						</div>
						<button
							onClick={onClose}
							className="w-8 h-8 flex items-center justify-center rounded-full
                                transition-colors hover:bg-gray-100 shrink-0"
							style={{border: "1.5px solid #E5E7EB"}}
						>
							<XMarkIcon className="w-4 h-4 text-gray-500"/>
						</button>
					</div>
				</div>

				{/* Body */}
				<div className="flex-1 overflow-y-auto px-6 py-5 thin-scrollbar flex flex-col gap-4">

					{/* Claim summary */}
					<div className="flex flex-col gap-1 p-3.5 rounded-xl"
					     style={{background: "#FAFAFA", border: "1.5px solid #E5E7EB"}}>
						<p className="font-custom-bold text-sm text-gray-900">
							{[claim.submittedFirstName, claim.submittedMiddleName, claim.submittedLastName]
								.filter(Boolean).join(" ") || "—"}
						</p>
						<p className="font-custom-medium text-[0.72rem] text-gray-500">
							{[claim.submittedPhone, claim.submittedEmail, claim.submittedFamilyTreeName]
								.filter(Boolean).join(" · ") || "—"}
						</p>
					</div>

					{/* Reason selection */}
					<div className="flex flex-col gap-1.5">
						<InputLabel required>Rejection Reason</InputLabel>
						<div className="flex flex-col gap-1.5">
							{REJECTION_REASONS.map(reason => (
								<button
									key={reason}
									type="button"
									onClick={() => setRejectionReason(reason)}
									className="flex items-center gap-3 p-3 rounded-xl text-left
                                        transition-all duration-150"
									style={{
										border: `1.5px solid ${rejectionReason === reason ? "#A20000" : "#E5E7EB"}`,
										background: rejectionReason === reason ? "#FDF0ED" : "#FAFAFA",
									}}
								>
									<div
										className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
										style={{
											border: `2px solid ${rejectionReason === reason ? "#A20000" : "#D1D5DB"}`,
											background: rejectionReason === reason ? "#A20000" : "#fff",
										}}
									>
										{rejectionReason === reason && (
											<div className="w-1.5 h-1.5 bg-white rounded-full"/>
										)}
									</div>
									<span className="font-custom-medium text-sm text-gray-700 select-none">
                                        {reason}
                                    </span>
								</button>
							))}
						</div>
					</div>

					{/* Other note */}
					{rejectionReason === "Other" && (
						<div className="flex flex-col gap-1">
							<InputLabel htmlFor="reject-note" required>Please specify</InputLabel>
							<input
								id="reject-note"
								type="text"
								value={rejectionNote}
								onChange={e => setRejectionNote(e.target.value)}
								placeholder="Describe the reason…"
								className={inputCls(!rejectionNote.trim() && rejectionReason === "Other")}
							/>
						</div>
					)}

					{/* Admin notes */}
					<div className="flex flex-col gap-1">
						<InputLabel>
							Admin Notes
							<span className="font-custom-regular text-gray-400 ml-1">(optional)</span>
						</InputLabel>
						<textarea
							rows={2}
							value={adminNotes}
							onChange={e => setAdminNotes(e.target.value)}
							placeholder="Add any notes…"
							className={inputCls(false) + " resize-none"}
						/>
					</div>
				</div>

				{/* Footer */}
				<div className="shrink-0 px-6 py-4 bg-white" style={{borderTop: "1px solid #F3F3F3"}}>
					<div className="flex items-center justify-between gap-3">
						<button
							onClick={onClose}
							disabled={isSubmitting}
							className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-600
                                transition-all duration-150 hover:bg-gray-100 disabled:opacity-50"
							style={{border: "1.5px solid #E5E7EB"}}
						>
							← Cancel
						</button>
						<button
							onClick={handleReject}
							disabled={!rejectionReason || isSubmitting}
							className="flex items-center gap-2 px-6 py-2.5 rounded-xl transform-gpu
                                font-custom-bold text-sm text-white transition-all duration-150
                                hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
							style={{
								background: "linear-gradient(135deg, #DC2626, #B91C1C)",
								boxShadow: rejectionReason ? "0 4px 14px rgba(220,38,38,0.30)" : "none",
							}}
						>
							{isSubmitting
								? <>
									<div
										className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0"/>
									Rejecting…</>
								: <><XMarkIcon className="h-4 w-4 shrink-0"/> Confirm Rejection</>
							}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export const ProfileClaimRequests = (props) => {
	const {selectedMenuOption, setSelectedMenuOption} = props;
	const axiosPrivate = useAxiosPrivate();

	// ── State ─────────────────────────────────────────────────────────────────
	const [claimsData, setClaimsData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [pageCount, setPageCount] = useState(0);
	const [totalClaims, setTotalClaims] = useState(0);
	const [isExporting, setIsExporting] = useState(false);

	const [globalSearch, setGlobalSearch] = useState("");
	const debouncedSearch = useDebounce(globalSearch, 500);

	const [columnFilters, setColumnFilters] = useState({});
	const [statusTab, setStatusTab] = useState("all");
	const [sorting, setSorting] = useState([{id: "createdAt", desc: true}]);
	const [columnVisibility, setColumnVisibility] = useState({
		status: true,
		adminNotes: false,
		// matchedAccountId: false,
	});
	const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 50});

	// Family tree names for dynamic filter
	const [familyTreeOptions, setFamilyTreeOptions] = useState([]);

	// Action modals
	const [resolveTarget, setResolveTarget] = useState(null);
	const [showResolve, setShowResolve] = useState(false);
	const [resolveVisible, setResolveVisible] = useState(false);
	const resolveRafRef = useRef(null);

	const [rejectTarget, setRejectTarget] = useState(null);
	const [showReject, setShowReject] = useState(false);
	const [rejectVisible, setRejectVisible] = useState(false);
	const rejectRafRef = useRef(null);

	const openResolve = (claim) => {
		setResolveTarget(claim);
		setShowResolve(true);
		cancelAnimationFrame(resolveRafRef.current);
		resolveRafRef.current = requestAnimationFrame(() => {
			resolveRafRef.current = requestAnimationFrame(() => setResolveVisible(true));
		});
	};

	const closeResolve = () => {
		setResolveVisible(false);
		setTimeout(() => {
			setShowResolve(false);
			setResolveTarget(null);
		}, 220);
	};

	const openReject = (claim) => {
		setRejectTarget(claim);
		setShowReject(true);
		cancelAnimationFrame(rejectRafRef.current);
		rejectRafRef.current = requestAnimationFrame(() => {
			rejectRafRef.current = requestAnimationFrame(() => setRejectVisible(true));
		});
	};

	const closeReject = () => {
		setRejectVisible(false);
		setTimeout(() => {
			setShowReject(false);
			setRejectTarget(null);
		}, 220);
	};

	// Cleanup:
	useEffect(() => () => {
		cancelAnimationFrame(resolveRafRef.current);
		cancelAnimationFrame(rejectRafRef.current);
	}, []);

	// ── Load distinct family tree names ───────────────────────────────────────
	useEffect(() => {
		getDistinctFamilyTreeNamesApi(axiosPrivate)
			.then(res => {
				if (res.success) {
					setFamilyTreeOptions([
						...BLANK_AWARE_OPTIONS,
						...res.data.map(n => ({label: n, value: n})),
					]);
				}
			})
			.catch(() => {
			}); // non-critical
	}, []);

	// Build COLUMN_FILTER_CONFIGS dynamically (family tree uses live data)
	const COLUMN_FILTER_CONFIGS = useMemo(() => ({
		submittedPhone: {options: BLANK_AWARE_OPTIONS},
		submittedEmail: {options: BLANK_AWARE_OPTIONS},
		submittedFamilyTreeName: {options: familyTreeOptions},
		status: {options: STATUS_OPTIONS},
		matchedAccountId: {options: BLANK_AWARE_OPTIONS},
		adminNotes: {options: BLANK_AWARE_OPTIONS},
	}), [familyTreeOptions]);

	// ── Fetch ─────────────────────────────────────────────────────────────────
	const fetchClaims = async () => {
		setIsLoading(true);
		try {
			const res = await getPaginatedClaimRequestsApi(axiosPrivate, {
				page: pagination.pageIndex + 1,
				limit: pagination.pageSize,
				globalSearch: debouncedSearch,
				sortField: sorting.length ? sorting[0].id : "createdAt",
				sortOrder: sorting.length && sorting[0].desc ? "desc" : "asc",
				filters: Object.keys(columnFilters).length > 0 ? columnFilters : undefined,
				statusTab: statusTab !== "all" ? statusTab : undefined,
			});
			if (res.success) {
				setClaimsData(res.data);
				setPageCount(res.pagination.totalPages);
				setTotalClaims(res.pagination.totalCount);
			} else {
				setClaimsData([]);
			}
		} catch {
			toast.error("Failed to fetch claim requests.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
			fetchClaims();
		},
		[pagination.pageIndex, pagination.pageSize, sorting, debouncedSearch, columnFilters, statusTab]);

	// ── Filter handlers ───────────────────────────────────────────────────────
	const resetPageAndSelection = () => setPagination(prev => ({...prev, pageIndex: 0}));

	const handleTabChange = (tab) => {
		setStatusTab(tab);
		// Hide status column on non-all tabs, show on all
		setColumnVisibility(prev => ({...prev, status: tab === "all"}));
		resetPageAndSelection();
	};

	const handleColumnFilterChange = (columnId, value) => {
		setColumnFilters(prev => {
			const next = {...prev};
			if (value === undefined) {
				delete next[columnId];
				return next;
			}
			if (Array.isArray(value) && value.length > 0) next[columnId] = value;
			else delete next[columnId];
			return next;
		});
		resetPageAndSelection();
	};

	const handleClearAllFilters = () => {
		setColumnFilters({});
		setGlobalSearch("");
		resetPageAndSelection();
	};

	const activeFilterChips = Object.entries(columnFilters).map(([key, values]) => {
		const config = COLUMN_FILTER_CONFIGS[key];
		const labels = values.map(v => config?.options.find(o => o.value === v)?.label ?? v);
		const display = labels.length > 2 ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}` : labels.join(", ");
		return {key, label: `${COLUMN_LABELS[key] ?? key}: ${display}`};
	});

	// ── Export ────────────────────────────────────────────────────────────────
	const handleExport = async () => {
		setIsExporting(true);
		try {
			const blob = await exportClaimRequestsApi(axiosPrivate, {
				globalSearch: debouncedSearch || undefined,
				filters: Object.keys(columnFilters).length > 0 ? columnFilters : undefined,
				statusTab: statusTab !== "all" ? statusTab : undefined,
				sortField: sorting.length ? sorting[0].id : "createdAt",
				sortOrder: sorting.length && sorting[0].desc ? "desc" : "asc",
			});
			const url = window.URL.createObjectURL(new Blob([blob]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `ProfileClaimRequests_${new Date().toISOString().split("T")[0]}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			toast.success("Export successful!");
		} catch {
			toast.error("Export failed. Please try again.");
		} finally {
			setIsExporting(false);
		}
	};

	// ── Column definitions ────────────────────────────────────────────────────
	const columns = useMemo(() => [

		// ID
		{
			id: "_id",
			enableHiding: false, enableSorting: false, size: 80,
			header: "ID",
			cell: ({row}) => {
				const id = row.original._id ?? "";
				return (
					<div className="flex items-center gap-1 group">
                        <span className="font-mono text-[10px] text-gray-400 tracking-tight" title={id}>
                            …{id.slice(-6)}
                        </span>
						<button
							onClick={(e) => {
								e.stopPropagation();
								navigator.clipboard.writeText(id);
								toast.success("ID copied!", {autoClose: 1500});
							}}
							className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-100 text-gray-400"
							title="Copy full ID"
						>
							<ClipboardDocumentIcon className="h-3 w-3"/>
						</button>
					</div>
				);
			},
		},

		// Submitted At
		{
			accessorKey: "createdAt",
			header: "Submitted At", size: 130,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-600">
                    {new Date(info.getValue()).toLocaleDateString("en-IN", {
	                    day: "2-digit",
	                    month: "short",
	                    year: "numeric"
                    })}
                  </span>
				: <span className="text-gray-300">—</span>,
		},

		// Name (computed)
		{
			id: "submittedName",
			accessorFn: row => [row.submittedFirstName, row.submittedMiddleName, row.submittedLastName].filter(Boolean).join(" "),
			enableHiding: false,
			header: "Name",
			cell: info => info.getValue()
				? <span className="font-custom-medium text-gray-900 text-sm">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// Phone
		{
			accessorKey: "submittedPhone",
			enableSorting: false, size: 160,
			header: "Phone",
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-700 text-xs">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// Email
		{
			accessorKey: "submittedEmail",
			header: "Email",
			cell: info => info.getValue()
				? <span
					className="font-custom-regular text-gray-700 text-xs truncate block max-w-[180px]">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// Family Tree
		{
			accessorKey: "submittedFamilyTreeName",
			header: "Family Tree", size: 160,
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-700 text-xs truncate block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// Status (only shown on "all" tab — visibility controlled in state)
		{
			accessorKey: "status",
			enableSorting: false, size: 150,
			header: "Status",
			cell: info => <StatusBadge status={info.getValue()}/>,
		},

		// Matched Account
		{
			accessorKey: "matchedAccountId",
			enableSorting: false,
			header: "Matched Account",
			cell: info => info.getValue()
				? <div className="flex items-center gap-1 group">
                    <span className="font-mono text-[10px] text-gray-400 tracking-tight" title={info.getValue()}>
                        …{info.getValue().slice(-18)}
                    </span>
					<button
						onClick={(e) => {
							e.stopPropagation();
							navigator.clipboard.writeText(info.getValue()).then(() => toast.success("ID copied!", {autoClose: 1500}));
						}}
						className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-100"
					>
						<ClipboardDocumentIcon className="h-3 w-3 text-gray-400"/>
					</button>
				</div>
				: <span className="text-gray-300">—</span>,
		},

		// Admin Notes
		{
			accessorKey: "adminNotes",
			enableSorting: false,
			header: "Admin Notes",
			cell: info => info.getValue()
				? <span className="text-xs text-gray-600 truncate block max-w-[200px]"
				        title={info.getValue()}>{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// Actions
		{
			id: "actions",
			enableHiding: false, enableSorting: false, size: 200,
			header: () => <div className="text-right">Actions</div>,
			cell: ({row}) => {
				const claim = row.original;
				const status = claim.status;
				const isDone = status === "RESOLVED" || status === "AUTO_RESOLVED" || status === "REJECTED";

				return (
					<div className="shrink-0 flex items-center gap-1.5">
						{!isDone && (
							<>
								<button
									onClick={() => openResolve(claim)}
									className="flex items-center gap-1 px-2 py-1 text-[11px] font-custom-semi-bold
                                        text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg
                                        hover:bg-emerald-100 transition-colors"
								>
									<CheckCircleIcon className="h-3.5 w-3.5"/> Resolve
								</button>
								<button
									onClick={() => openReject(claim)}
									className="flex items-center gap-1 px-2 py-1 text-[11px] font-custom-semi-bold
                                        text-red-600 bg-red-50 border border-red-200 rounded-lg
                                        hover:bg-red-100 transition-colors"
								>
									<XCircleIcon className="h-3.5 w-3.5"/> Reject
								</button>
							</>
						)}
						{isDone && (
							<span className="text-xs text-gray-400 font-custom-medium pr-1">
                                {status === "REJECTED" ? "Rejected" : "Resolved"}
                            </span>
						)}
					</div>
				);
			},
		},
	], []);

	// ── Table ─────────────────────────────────────────────────────────────────
	const table = useReactTable({
		data: claimsData,
		columns,
		state: {sorting, pagination, columnVisibility},
		pageCount,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		onPaginationChange: setPagination,
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
	});

	// ─────────────────────────────────────────────────────────────────────────
	// RENDER
	// ─────────────────────────────────────────────────────────────────────────

	return (
		<div className="flex flex-col h-full min-h-0 overflow-hidden gap-3 p-4">

			{/* Page header */}
			<div className="flex items-center gap-2 shrink-0">
				<FlyoutSuperUserNavigationMenu
					selectedMenuOption={selectedMenuOption}
					setSelectedMenuOption={setSelectedMenuOption}
				/>
				<h1 className="font-custom-bold text-gray-900
                    portrait:text-[1.25rem] portrait:jk:text-[1.5rem]
                    landscape:text-[0.875rem] landscape:jk:text-[1rem]">
					Profile Claims
				</h1>
			</div>

			{/* Status tabs */}
			<div className="flex items-end gap-0.5 border-b border-gray-100 overflow-x-auto no-scrollbar shrink-0">
				{CLAIM_STATUS_TABS.map(tab => {
					const isActive = statusTab === tab.id;
					return (
						<button key={tab.id} onClick={() => handleTabChange(tab.id)}
						        className={`px-3.5 py-2 text-sm font-custom-semi-bold rounded-t-lg whitespace-nowrap transition-all
                                ${isActive
							        ? "text-[#A20000] border-b-2 border-[#A20000] -mb-px bg-white"
							        : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
						        }`}>
							{tab.label}
							{isActive && totalClaims > 0 && (
								<span
									className="ml-1.5 text-[10px] font-custom-bold bg-[#A20000]/10 text-[#A20000] px-1.5 py-0.5 rounded-full">
                                    {totalClaims.toLocaleString("en-IN")}
                                </span>
							)}
						</button>
					);
				})}
			</div>

			{/* Toolbar */}
			<div className="flex items-center gap-2 shrink-0">
				{/* Search */}
				<div className="relative flex-1 min-w-0">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
					<input
						type="text"
						placeholder="Search name, phone, email, family tree…"
						value={globalSearch}
						onChange={e => {
							setGlobalSearch(e.target.value);
							resetPageAndSelection();
						}}
						className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm
                            focus:outline-none focus:ring-1 focus:ring-[#D26A53] transition-all
                            shadow-sm font-custom-medium"
					/>
					{globalSearch && (
						<button onClick={() => {
							setGlobalSearch("");
							resetPageAndSelection();
						}}
						        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
							<XMarkIcon className="h-3.5 w-3.5"/>
						</button>
					)}
				</div>

				{/* Export */}
				<button onClick={handleExport} disabled={isExporting}
				        className="p-2 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700 transition-colors"
				        title="Export to Excel">
					{isExporting
						? <ArrowPathIcon className="h-4 w-4 animate-spin"/>
						: <ArrowDownTrayIcon className="h-4 w-4"/>
					}
				</button>

				{/* Column visibility */}
				<Popover className="relative">
					<Popover.Button
						className="p-2 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700 outline-none"
						title="Toggle columns">
						<AdjustmentsHorizontalIcon className="h-4 w-4"/>
					</Popover.Button>
					<Transition as={Fragment}
					            enter="transition duration-100 ease-out" enterFrom="transform scale-95 opacity-0"
					            enterTo="transform scale-100 opacity-100"
					            leave="transition duration-75 ease-out" leaveFrom="transform scale-100 opacity-100"
					            leaveTo="transform scale-95 opacity-0">
						<Popover.Panel
							className="absolute right-0 z-10 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl p-2">
							<p className="px-2 py-1.5 text-[10px] font-custom-semi-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1 sticky top-0 bg-white">
								Toggle Columns
							</p>
							<div className="flex flex-col gap-0.5 mt-1 max-h-64 overflow-y-auto thin-scrollbar">
								{table.getAllLeafColumns().map(col => {
									if (!col.getCanHide()) return null;
									return (
										<label key={col.id}
										       className="flex items-center gap-2.5 cursor-pointer text-sm font-custom-medium text-gray-700 hover:bg-gray-50 px-2 py-1.5 rounded-lg">
											<input type="checkbox" checked={col.getIsVisible()}
											       onChange={col.getToggleVisibilityHandler()}
											       className="rounded border-gray-300 text-[#D26A53] focus:ring-[#D26A53] h-3.5 w-3.5"/>
											{typeof col.columnDef.header === "string" ? col.columnDef.header : col.id}
										</label>
									);
								})}
							</div>
						</Popover.Panel>
					</Transition>
				</Popover>
			</div>

			{/* Active filter chips */}
			{activeFilterChips.length > 0 && (
				<div className="flex items-center gap-2 flex-wrap shrink-0">
                    <span className="text-[11px] font-custom-semi-bold text-gray-400 flex items-center gap-1 shrink-0">
                        <FunnelIcon className="h-3.5 w-3.5"/> Active filters:
                    </span>
					{activeFilterChips.map(chip => (
						<span key={chip.key}
						      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-custom-semi-bold bg-[#D26A53]/10 text-[#D26A53] border border-[#D26A53]/20">
                            {chip.label}
							<button onClick={() => handleColumnFilterChange(chip.key, undefined)}
							        className="ml-0.5 hover:text-[#A20000]">
                                <XMarkIcon className="h-3 w-3"/>
                            </button>
                        </span>
					))}
					<button onClick={handleClearAllFilters}
					        className="text-xs font-custom-semi-bold text-gray-400 hover:text-gray-700 underline underline-offset-2">
						Clear all
					</button>
				</div>
			)}

			{/* Table */}
			<div
				className="flex-1 min-h-0 min-w-0 relative shadow-sm rounded-xl"
				style={{clipPath: "inset(0 round 0.75rem)"}}
			>
				{/* Absolute Border */}
				<div className="absolute inset-0 rounded-xl border border-gray-200 pointer-events-none z-10"/>

				{/* Loading Overlay */}
				{isLoading && (
					<div
						className="absolute inset-0 bg-white/70 z-10 flex flex-col items-center justify-center gap-2 backdrop-blur-[1px]">
						<div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#A20000]"/>
						<p className="text-xs font-custom-medium text-gray-400">Fetching claims…</p>
					</div>
				)}

				{/* Table */}
				<div
					className="absolute inset-0 overflow-auto thin-scrollbar bg-white rounded-xl">
					<table className="w-full min-w-[50rem] table-fixed text-left text-sm">
						<thead className="sticky top-0 z-[5] bg-gray-50/80 backdrop-blur-sm border-b border-gray-200">
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => {
									const canSort = header.column.getCanSort();
									const isSorted = header.column.getIsSorted();
									const filterKey = header.column.columnDef.accessorKey ?? header.column.id;
									const filterOpts = COLUMN_FILTER_CONFIGS[filterKey]?.options;

									return (
										<th
											key={header.id}
											style={{width: header.getSize()}}
											className="h-10 px-3 text-[10px] font-custom-semi-bold text-gray-500 tracking-wider whitespace-nowrap align-middle"
										>
											{header.isPlaceholder ? null : (
												<div className="flex items-center gap-1">
													<div
														className={`flex items-center gap-1 uppercase ${canSort ? "cursor-pointer select-none hover:text-gray-900 transition-colors" : ""}`}
														onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
													>
														{flexRender(header.column.columnDef.header, header.getContext())}
														{isSorted === "asc" &&
															<ChevronUpIcon className="h-3 w-3 text-[#D26A53]"/>}
														{isSorted === "desc" &&
															<ChevronDownIcon className="h-3 w-3 text-[#D26A53]"/>}
													</div>
													{filterOpts && (
														<ColumnFilterPopover
															key={JSON.stringify(columnFilters[filterKey] ?? [])}
															columnId={filterKey}
															currentValue={columnFilters[filterKey]}
															onFilterChange={handleColumnFilterChange}
															configOptions={filterOpts}
														/>
													)}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
						</thead>

						<tbody className="divide-y divide-gray-50">
						{table.getRowModel().rows.length > 0 ? (
							table.getRowModel().rows.map(row => (
								<tr key={row.id} className="hover:bg-gray-50/60 transition-colors">
									{row.getVisibleCells().map(cell => (
										<td key={cell.id}
										    style={{width: cell.column.getSize(), maxWidth: cell.column.getSize()}}
										    className="px-3 py-2.5 text-gray-800 whitespace-nowrap font-custom-regular overflow-hidden">
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</td>
									))}
								</tr>
							))
						) : (
							!isLoading && (
								<tr>
									<td colSpan={columns.length} className="h-48 text-center">
										<div className="flex flex-col items-center gap-2 text-gray-400">
											<MagnifyingGlassIcon className="h-10 w-10 opacity-20"/>
											<p className="font-custom-medium text-sm">No claim requests found</p>
											<p className="text-xs">Try adjusting your search or filters</p>
										</div>
									</td>
								</tr>
							)
						)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Pagination */}
			<div className="flex items-center justify-between gap-2 pt-0.5 shrink-0">
				<div className="text-xs text-gray-500 font-custom-medium">
					{totalClaims.toLocaleString("en-IN")} total claims
				</div>
				<div className="flex items-center gap-2">
					<div className="hidden sm:flex items-center gap-2">
						<p className="text-xs font-custom-medium text-gray-600">Rows</p>
						<select
							value={table.getState().pagination.pageSize}
							onChange={e => table.setPageSize(Number(e.target.value))}
							className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white
                                font-custom-medium outline-none focus:ring-1 focus:ring-[#D26A53] text-gray-700">
							{[25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
						</select>
					</div>
					<div className="text-xs font-custom-medium text-gray-600 whitespace-nowrap">
						{table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
					</div>
					<div className="flex items-center gap-1">
						<button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}
						        className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors hidden sm:block">
							<ChevronDoubleLeftIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}
						        className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
							<ChevronLeftIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}
						        className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
							<ChevronRightIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.setPageIndex(table.getPageCount() - 1)}
						        disabled={!table.getCanNextPage()}
						        className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors hidden sm:block">
							<ChevronDoubleRightIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
					</div>
				</div>
			</div>

			{/* ── Resolve Modal ── */}
			{showResolve && resolveTarget && (
				<ResolveModal
					claim={resolveTarget}
					axiosPrivate={axiosPrivate}
					visible={resolveVisible}
					onClose={closeResolve}
					onResolved={() => {
						fetchClaims();
						closeResolve();
					}}
				/>
			)}

			{/* ── Reject Modal ── */}
			{showReject && rejectTarget && (
				<RejectModal
					claim={rejectTarget}
					axiosPrivate={axiosPrivate}
					visible={rejectVisible}
					onClose={closeReject}
					onRejected={() => {
						fetchClaims();
						closeReject();
					}}
				/>
			)}
		</div>
	);
};